import { useQuery } from '@tanstack/react-query';
import { RefreshCw, WifiOff } from 'lucide-react';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../auth/AuthProvider';
import { useHealth } from './HealthProvider';
import { MediaServerPanel } from './MediaServerPanel';
import { RecordingHealthPanel } from './RecordingHealthPanel';
import { dependencyImpact, dependencyLabels, type DependencyCheck } from './health';
import { formatUptime } from './media';
import {
  containerView,
  getServices,
  serviceRoles,
  servicesKey,
  type ServiceInfo,
  type ServicesReport,
} from './services';
import { systemSectionId, systemStateView } from './systemState';

const checkedAtFormat = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'medium',
});

interface Row {
  name: string;
  /** Null for the API itself: it answered, so it is up, but it is not a check. */
  check: DependencyCheck | null;
  info: ServiceInfo | undefined;
}

/**
 * The system's details (0.67.0: formerly the "Sistema" page, now a section of
 * the platform's "Painel"): overall state, the services the control plane
 * depends on with versions and containers, recording and the media server.
 * Everything but readiness needs system.read, held by super administrators.
 */
export function SystemDetails() {
  const { state, checks, checkedAt, refreshing, refresh } = useHealth();
  const view = systemStateView[state];
  const { can, request } = useAuth();
  const admin = can('system.read');

  // Versions and containers are admin-only: never on the public health endpoint.
  const services = useQuery({
    queryKey: servicesKey,
    queryFn: () => getServices(request),
    enabled: admin && state !== 'unreachable',
    refetchInterval: 30_000,
  });
  const byName = new Map((services.data?.items ?? []).map((s) => [s.name, s]));
  const rows: Row[] = [
    ...(byName.has('api') ? [{ name: 'api', check: null, info: byName.get('api') }] : []),
    ...checks.map((c) => ({ name: c.name, check: c, info: byName.get(c.name) })),
  ];
  const showDocker = admin && services.data !== undefined && services.data.docker !== 'disabled';

  return (
    <section
      id={systemSectionId}
      aria-labelledby="system-heading"
      tabIndex={-1}
      className="scroll-mt-4 space-y-4 pt-2 focus:outline-none"
    >
      <div>
        <h2 id="system-heading" className="text-sm font-semibold tracking-tight text-fg">
          Sistema
        </h2>
        <p className="mt-0.5 text-xs text-fg-muted">
          Estado dos serviços dos quais o plano de controle depende.
        </p>
      </div>

      <section
        aria-labelledby="overall-heading"
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <h3 id="overall-heading" className="text-[13px] font-medium text-fg">
            Estado geral
          </h3>
          <StatusBadge tone={view.tone} pulse={state === 'checking'}>
            {view.label}
          </StatusBadge>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-fg-subtle" aria-live="polite">
            {checkedAt
              ? `Última verificação: ${checkedAtFormat.format(checkedAt)}`
              : 'Verificando…'}
          </p>
          <Button
            onClick={() => {
              refresh();
              if (admin) void services.refetch();
            }}
            disabled={refreshing}
          >
            <RefreshCw
              aria-hidden="true"
              className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`}
              strokeWidth={2}
            />
            {refreshing ? 'Atualizando…' : 'Atualizar'}
          </Button>
        </div>
      </section>

      <section
        aria-labelledby="deps-heading"
        className="overflow-hidden rounded-lg border border-line bg-surface"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          <h3 id="deps-heading" className="text-[13px] font-medium">
            Dependências
          </h3>
          {admin && <DockerNote report={services.data} failed={services.isError} />}
        </div>
        {state === 'unreachable' ? (
          <EmptyState
            tone="crit"
            icon={WifiOff}
            title="API indisponível"
            description="Não foi possível contatar o plano de controle. Verifique se o serviço da API está em execução e tente novamente."
            action={
              <Button variant="primary" onClick={refresh} disabled={refreshing}>
                Tentar novamente
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                <tr className="border-b border-line">
                  <th scope="col" className="px-4 py-2 font-medium">
                    Serviço
                  </th>
                  {admin && (
                    <th scope="col" className="px-4 py-2 font-medium">
                      Versão
                    </th>
                  )}
                  {showDocker && (
                    <th scope="col" className="px-4 py-2 font-medium">
                      Contêiner
                    </th>
                  )}
                  <th scope="col" className="px-4 py-2 font-medium">
                    Estado
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Latência
                  </th>
                </tr>
              </thead>
              <tbody>
                {state === 'checking' && checks.length === 0
                  ? [0, 1, 2].map((i) => <SkeletonRow key={i} cols={3 + (admin ? 1 : 0)} />)
                  : rows.map((r) => (
                      <tr key={r.name} className="border-b border-line last:border-0">
                        <td className="px-4 py-2.5">
                          <div className="text-fg">{dependencyLabels[r.name] ?? r.name}</div>
                          {serviceRoles[r.name] && (
                            <div className="text-2xs text-fg-subtle">{serviceRoles[r.name]}</div>
                          )}
                          {r.check && r.check.status !== 'up' && dependencyImpact[r.name] && (
                            <div className="text-2xs text-fg-muted">{dependencyImpact[r.name]}</div>
                          )}
                        </td>
                        {admin && (
                          <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                            {services.isPending ? (
                              <span className="inline-block h-3 w-16 animate-pulse rounded bg-surface-hover" />
                            ) : (
                              (r.info?.version ?? '—')
                            )}
                          </td>
                        )}
                        {showDocker && (
                          <td className="px-4 py-2.5">
                            <ContainerCell info={r.info} at={services.data.checked_at} />
                          </td>
                        )}
                        <td className="px-4 py-2.5">
                          <StatusBadge
                            tone={
                              !r.check || r.check.status === 'up'
                                ? 'ok'
                                : r.check.optional
                                  ? 'warn'
                                  : 'crit'
                            }
                          >
                            {!r.check || r.check.status === 'up' ? 'Disponível' : 'Indisponível'}
                          </StatusBadge>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                          {r.check ? `${String(r.check.latency_ms)} ms` : '—'}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {admin && state !== 'unreachable' && <RecordingHealthPanel />}
      {admin && state !== 'unreachable' && <MediaServerPanel />}
    </section>
  );
}

function DockerNote({ report, failed }: { report: ServicesReport | undefined; failed: boolean }) {
  if (failed) return <p className="text-xs text-warn">Versões e contêineres indisponíveis.</p>;
  if (!report) return null;
  if (report.docker === 'unavailable') {
    return (
      <p className="text-xs text-warn">
        Estado dos contêineres indisponível: o proxy da API do Docker não respondeu.
      </p>
    );
  }
  if (report.docker === 'disabled') {
    return (
      <p className="text-xs text-fg-subtle">Estado dos contêineres desativado nesta implantação.</p>
    );
  }
  return null;
}

function ContainerCell({ info, at }: { info: ServiceInfo | undefined; at: string | undefined }) {
  const c = info?.container;
  if (!c) return <span className="text-xs text-fg-subtle">—</span>;
  const v = containerView(c);
  // Uptime at the time of the reading (keeps rendering pure).
  const since = c.started_at && at ? (Date.parse(at) - Date.parse(c.started_at)) / 1000 : null;
  return (
    <div>
      <StatusBadge tone={v.tone}>{v.label}</StatusBadge>
      <div className="mt-0.5 text-2xs text-fg-subtle">
        {c.state === 'running' && since !== null && `há ${formatUptime(since)} · `}
        <span className={c.restart_count > 0 ? 'text-warn' : ''}>
          {c.restart_count === 1 ? '1 reinício' : `${String(c.restart_count)} reinícios`}
        </span>
        <span className="font-mono" title={c.image}>
          {' '}
          · {c.image.split('@')[0]}
        </span>
      </div>
    </div>
  );
}

function SkeletonRow({ cols }: { cols: number }) {
  const widths = ['w-24', 'w-16', 'w-20', 'ml-auto w-12'].slice(-cols);
  return (
    <tr aria-hidden="true" className="border-b border-line last:border-0">
      {widths.map((w) => (
        <td key={w} className="px-4 py-3">
          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
        </td>
      ))}
    </tr>
  );
}
